import { cloneElement, isValidElement, useEffect, useId, useState } from 'react';
import { Building2 } from 'lucide-react';
import { PARTNER_STATUS_LABEL, PARTNER_TYPE_LABEL, type Partner } from '@dru/types';
import {
  AddressBlock, Alert, Button, ContactBlock, Detail, DetailSection, ErrorState,
  InfoTip, Input, StatusBadge, TextSkeleton, addressForm, addressPayload,
  type AddressFormValue, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { AddressFields } from '../../../components/AddressFields';
import { VenueCatalogue } from '../components/VenueCatalogue';
import { VenueSections } from '../components/VenueSections';
import { VenueReadiness } from '../components/VenueReadiness';
import { Link } from 'react-router-dom';
import { KeyFigures } from '@dru/ui';

/**
 * The venue's own record.
 *
 * Editable here because nobody else knows when a hospital's phone number,
 * opening hours or daily capacity change — and a venue that cannot correct
 * its own address ends up correcting it through support, which means it does
 * not get corrected.
 *
 * What is deliberately NOT editable here is the capability list: what a venue
 * can perform decides where workers are routed, so a venue declaring its own
 * would be a claim nobody checked. The same goes for its status and its type.
 * Those stay with the platform, and the screen says so rather than leaving
 * somebody hunting for the button.
 */
export function Profile() {
  const { principal, permissions } = useAuth();
  // A technician can read the venue's profile and cannot change it; the button
  // is offered only to someone whose save the API would accept.
  const canEdit = holds(permissions, 'partner.update');
  const venue = principal?.partner;
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);

  const partner = useQuery<Partner & Record<string, unknown>>(
    () => (venue ? api.get(`/partners/${venue.id}`) : Promise.reject(new Error('no venue'))),
    [venue?.id],
  );
  // For the readiness checklist and the day's work. Each read is the one its
  // own page makes, and only for a role that page is open to.
  const canStaff = holds(permissions, 'user.read');
  const canQueue = holds(permissions, 'encounter.read');
  const canCamps = holds(permissions, 'partner.update');
  const staff = useQuery<{ items: Array<{ id: string; status: string; fullName: string; roles: Array<{ key: string }>; signingProfile?: { signatureImage: boolean; registration: boolean; registrationExpected: boolean } | null }> } | null>(
    () => (venue && canStaff ? api.get(`/partners/${venue.id}/users`) : Promise.resolve(null)), [venue?.id, canStaff]);
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  const queue = useQuery<{ awaitingCheckIn: unknown[]; inProgress: unknown[]; completed: unknown[] } | null>(
    () => (venue && canQueue ? api.get(`/encounters/queue?partnerId=${venue.id}&limit=100&date=${today}`) : Promise.resolve(null)),
    [venue?.id, canQueue, today]);
  const camps = useQuery<{ camps: Array<{ status: string }> } | null>(
    () => (canCamps ? api.get('/camp/camps') : Promise.resolve(null)), [canCamps]);

  if (!venue) {
    return (
      <Alert tone="danger">
        This screen belongs to a hospital or laboratory account. Yours is not one.
      </Alert>
    );
  }
  if (partner.loading) return <TextSkeleton lines={8} />;
  if (partner.error) return <ErrorState title="Could not load the venue’s profile" detail={partner.error} onRetry={partner.reload} />;

  const p = partner.data as Record<string, any>;

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title={(
          <span className="flex items-center gap-2">
            <Building2 className="h-5 w-5 shrink-0 text-neutral-400" aria-hidden />
            {p?.name}
          </span>
        )}
        hint={(
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono">{p?.code}</span>
            <span aria-hidden>·</span>
            <span>{PARTNER_TYPE_LABEL[String(p?.type)] ?? String(p?.type ?? '')}</span>
            <StatusBadge tone={p?.status === 'ACTIVE' ? 'covered' : p?.status === 'ONBOARDING' ? 'pending' : 'neutral'}>
              {PARTNER_STATUS_LABEL[p?.status as keyof typeof PARTNER_STATUS_LABEL] ?? String(p?.status ?? '')}
            </StatusBadge>
          </span>
        )}
        actions={!editing && canEdit ? <Button onClick={() => setEditing(true)}>Edit details</Button> : undefined}
      />

      <VenueSections />

      {editing ? (
        <EditForm
          venueId={venue.id}
          partner={p}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            partner.reload();
            toast('success', 'Saved. The platform sees the change straight away.');
          }}
        />
      ) : (
        <>
          {(queue.data || camps.data) && (
            <KeyFigures
              label="Today at your venue"
              figures={[
                queue.data && { key: 'expected', label: 'Expected today', value: queue.data.awaitingCheckIn.length,
                  hint: <Link to="/partner/queue" className="text-primary-600 hover:text-primary-700">Open the queue →</Link> },
                queue.data && { key: 'progress', label: 'In progress', value: queue.data.inProgress.length,
                  tone: queue.data.inProgress.length ? 'pending' : undefined },
                queue.data && { key: 'done', label: 'Completed today', value: queue.data.completed.length },
                camps.data && { key: 'camps', label: 'Camps running or upcoming',
                  value: camps.data.camps.filter((c) => c.status !== 'FINISHED').length,
                  hint: <Link to="/partner/camps" className="text-primary-600 hover:text-primary-700">See camps →</Link> },
              ]}
            />
          )}

          <VenueReadiness
            partner={p}
            staff={staff.data?.items ?? null}
            canEdit={canEdit}
            canAddStaff={holds(permissions, 'user.create')}
            onEdit={() => setEditing(true)}
          />

          <DetailSection title="Where">
            <AddressBlock address={p as never} />
          </DetailSection>

          <DetailSection title="Who to call">
            <ContactBlock contact={p as never} />
          </DetailSection>

          <DetailSection
            title="When, and how many"
            action={(
              <InfoTip label="About daily capacity">
                Capacity is what booking checks before it offers a slot here, so keeping it honest
                keeps the queue honest.
              </InfoTip>
            )}
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Detail label="Operating days" value={p?.operatingDays || 'Not set'} />
              <Detail label="Operating hours" value={p?.operatingHours || 'Not set'} />
              <Detail label="Daily capacity" value={p?.dailyCapacity != null ? `${p.dailyCapacity} employees a day` : 'Not set'} />
            </div>
          </DetailSection>

          <Branches rows={(p?.locations ?? []) as BranchRow[]} />

          {/* The LIST is the platform's: what a venue can perform decides
              where workers are sent, and a venue declaring its own would be
              a claim nobody checked. The TERMS on each test — the venue's
              code, its turnaround, a pause while the analyzer is away — are
              the venue's own knowledge, and nobody else has it. */}
          {/* Folded: a venue's list runs to hundreds of tests, and it is
              reference, not the day's work. */}
          <details className="group rounded-card border border-neutral-200 bg-white">
            <summary className="cursor-pointer px-5 py-3 text-small font-medium text-neutral-800 hover:text-neutral-900 max-md:py-3.5">
              Tests you perform ({(p?.capabilities ?? []).length})
              <span className="ml-2 font-normal text-neutral-500">your codes, turnaround and pauses</span>
            </summary>
            <div className="p-4 pt-0">
              <VenueCatalogue
                partnerId={venue.id}
                rows={(p?.capabilities ?? []) as never}
                canEdit={canEdit}
                canImport={false}
                onSaved={() => { partner.reload(); toast('success', 'Saved. Routing reads the change straight away.'); }}
              />
            </div>
          </details>
          <p className="flex items-center gap-1 text-caption text-neutral-500">
            Ask support to add or remove a test or a branch.
            <InfoTip label="About who holds the test list">
              Which tests are on this list, your branches, your type and your empanelment status are held by
              the platform.
            </InfoTip>
          </p>
        </>
      )}
    </div>
  );
}

function EditForm({ venueId, partner, onCancel, onSaved }: {
  venueId: string;
  partner: Record<string, any>;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: partner.name ?? '',
    contactName: partner.contactName ?? '',
    contactPhone: partner.contactPhone ?? '',
    contactAltPhone: partner.contactAltPhone ?? '',
    contactEmail: partner.contactEmail ?? '',
    emergencyPhone: partner.emergencyPhone ?? '',
    website: partner.website ?? '',
    operatingDays: partner.operatingDays ?? '',
    operatingHours: partner.operatingHours ?? '',
    dailyCapacity: String(partner.dailyCapacity ?? ''),
  });
  const [address, setAddress] = useState<AddressFormValue>(() => addressForm(partner as never));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setAddress(addressForm(partner as never)); }, [partner]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    setBusy(true); setError(null);
    try {
      // Empty strings are left out rather than sent: the server treats a
      // present key as a change, and "" would clear a field somebody simply
      // did not touch.
      const text = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => k !== 'dailyCapacity' && String(v).trim() !== ''),
      );
      await api.patch(`/partners/${venueId}`, {
        ...text,
        ...(form.dailyCapacity.trim() ? { dailyCapacity: Number(form.dailyCapacity) } : {}),
        ...addressPayload(address),
      });
      onSaved();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <DetailSection title="Identity">
        <label className="block max-w-md">
          <span className="text-caption text-neutral-700">Name <span className="text-danger-600" aria-hidden>*</span></span>
          <Input value={form.name} onChange={set('name')} className="mt-1 w-full" required aria-required="true" />
        </label>
      </DetailSection>

      <DetailSection title="Where">
        <AddressFields value={address} onChange={setAddress} />
      </DetailSection>

      <DetailSection title="Who to call">
        <div className="grid gap-4 sm:grid-cols-2">
          <Labelled label="Contact name"><Input value={form.contactName} onChange={set('contactName')} /></Labelled>
          <Labelled label="Contact email"><Input type="email" value={form.contactEmail} onChange={set('contactEmail')} /></Labelled>
          <Labelled label="Phone"><Input value={form.contactPhone} onChange={set('contactPhone')} inputMode="numeric" /></Labelled>
          <Labelled label="Alternate phone"><Input value={form.contactAltPhone} onChange={set('contactAltPhone')} inputMode="numeric" /></Labelled>
          <Labelled label="Emergency phone" info="Used when a critical value has to reach somebody tonight.">
            <Input value={form.emergencyPhone} onChange={set('emergencyPhone')} inputMode="numeric" placeholder="10 digits" />
          </Labelled>
          <Labelled label="Website"><Input value={form.website} onChange={set('website')} /></Labelled>
        </div>
      </DetailSection>

      <DetailSection title="When, and how many">
        <div className="grid gap-4 sm:grid-cols-3">
          <Labelled label="Operating days">
            <Input value={form.operatingDays} onChange={set('operatingDays')} placeholder="Mon–Sat" />
          </Labelled>
          <Labelled label="Operating hours">
            <Input value={form.operatingHours} onChange={set('operatingHours')} placeholder="08:00–18:00" />
          </Labelled>
          <Labelled label="Daily capacity" info="How many employees you can see in a day. Booking refuses a slot past it.">
            <Input value={form.dailyCapacity} onChange={set('dailyCapacity')} inputMode="numeric" />
          </Labelled>
        </div>
      </DetailSection>

      {error && <Alert tone="danger">{error}</Alert>}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <p className="mr-auto text-caption text-neutral-500">Blank fields are left as they were.</p>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={form.name.trim().length < 2}>Save changes</Button>
      </div>
    </div>
  );
}

function Labelled({ label, info, children }: {
  label: string; info?: React.ReactNode; children: React.ReactNode;
}) {
  const id = useId();
  // The ⓘ is a button, and a button inside a <label> would take the
  // label's clicks, so with one the label names the control by id instead.
  if (info && isValidElement<{ id?: string }>(children)) {
    return (
      <div>
        <div className="flex items-center gap-1">
          <label htmlFor={id} className="text-caption text-neutral-700">{label}</label>
          <InfoTip label={`About ${label}`}>{info}</InfoTip>
        </div>
        <span className="mt-1 block">{cloneElement(children, { id })}</span>
      </div>
    );
  }
  return (
    <label className="block">
      <span className="text-caption text-neutral-700">{label}</span>
      <span className="mt-1 block">{children}</span>
    </label>
  );
}

interface BranchRow {
  id: string;
  code: string;
  name: string;
  isPrimary: boolean;
  status: string;
  formattedAddress?: string | null;
  mapsUrl?: string | null;
  contactPhone?: string | null;
  operatingHours?: string | null;
}

/**
 * Where workers are actually sent. Booking offers a branch, not the venue, so
 * the venue should be able to see the list it is being routed by — read-only,
 * because adding or closing a branch changes routing and stays with the
 * platform.
 */
function Branches({ rows }: { rows: BranchRow[] }) {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  return (
    <DetailSection title={`Branches (${rows.length})`}>
      <ul className="divide-y divide-neutral-100">
        {sorted.map((b) => (
          <li key={b.id} className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1 py-3 first:pt-0 last:pb-0">
            <div className="min-w-0 flex-[1_1_18rem]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-small font-medium text-neutral-900">{b.name}</span>
                {b.isPrimary && <StatusBadge tone="info">Main site</StatusBadge>}
                {b.status !== 'ACTIVE' && <StatusBadge tone="neutral">Closed to bookings</StatusBadge>}
              </div>
              <div className="font-mono text-caption text-neutral-500">{b.code}</div>
              {b.formattedAddress && <div className="mt-0.5 text-small text-neutral-700">{b.formattedAddress}</div>}
            </div>
            <div className="text-small text-neutral-700 sm:text-right">
              {b.operatingHours && <div>{b.operatingHours}</div>}
              {b.contactPhone && (
                <a href={`tel:${b.contactPhone}`} className="inline-block py-1 text-primary-700 hover:underline max-md:py-2.5">{b.contactPhone}</a>
              )}
              {b.mapsUrl && (
                <div>
                  <a href={b.mapsUrl} target="_blank" rel="noreferrer" className="inline-block py-1 text-primary-700 hover:underline max-md:py-2.5">
                    Open in Maps<span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}
